/**
 * Digest Settings Component for Re:Nav-96
 *
 * Allows members to configure their email digest subscription preferences
 * for message board notifications. Users can set digest frequency per group
 * or apply a global default.
 *
 * Features:
 * - Per-group digest frequency selection (daily, weekly, none)
 * - Global default setting for all groups
 * - Loading and saving states
 * - Error handling with retry
 *
 * @example
 * ```tsx
 * <DigestSettings />
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import { foldersApi, groupsApi } from '@/services/api';
import type { Group } from '@/types';
import './DigestSettings.css';

/**
 * Digest frequency options
 */
type DigestFrequency = 'daily' | 'weekly' | 'none';

/**
 * Props for the DigestSettings component
 */
interface DigestSettingsProps {
  /** Optional CSS class name for container */
  className?: string;
}

/**
 * Group digest preference state
 */
interface GroupDigestPreference {
  groupId: string;
  groupName: string;
  folderId: string;
  folderName: string;
  frequency: DigestFrequency;
  isSaving: boolean;
}

/**
 * Frequency option configuration
 */
interface FrequencyOption {
  value: DigestFrequency;
  label: string;
  description: string;
}

/**
 * Available frequency options
 */
const frequencyOptions: FrequencyOption[] = [
  {
    value: 'daily',
    label: 'Daily',
    description: 'Receive a summary every day',
  },
  {
    value: 'weekly',
    label: 'Weekly',
    description: 'Receive a summary once a week',
  },
  {
    value: 'none',
    label: 'None',
    description: 'No digest emails for this group',
  },
];

/**
 * Loading skeleton for the digest settings
 */
function DigestSettingsSkeleton() {
  return (
    <div className="digest-settings-loading">
      <div className="digest-skeleton-header" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="digest-skeleton-item">
          <div className="digest-skeleton-group" />
          <div className="digest-skeleton-options" />
        </div>
      ))}
    </div>
  );
}

/**
 * Empty state when no groups are available
 */
function DigestSettingsEmptyState() {
  return (
    <div className="digest-settings-empty">
      <svg
        className="w-12 h-12 text-white/20 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
      <span className="text-white/50 text-sm text-center">
        No groups available to configure.
        <br />
        Join some groups to set up digest preferences.
      </span>
    </div>
  );
}

/**
 * Error state component
 */
function DigestSettingsError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="digest-settings-error">
      <svg
        className="w-12 h-12 text-red-400/60 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <span className="text-white/70 text-sm mb-4">{message}</span>
      <button
        onClick={onRetry}
        className="btn-secondary text-sm px-4 py-2"
      >
        Try Again
      </button>
    </div>
  );
}

/**
 * Individual group digest preference row
 */
function GroupDigestRow({
  preference,
  onFrequencyChange,
}: {
  preference: GroupDigestPreference;
  onFrequencyChange: (groupId: string, frequency: DigestFrequency) => void;
}) {
  return (
    <div className="digest-group-row">
      <div className="digest-group-info">
        <span className="digest-group-name">{preference.groupName}</span>
        <span className="digest-folder-name">{preference.folderName}</span>
      </div>
      <div className="digest-frequency-selector">
        {frequencyOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onFrequencyChange(preference.groupId, option.value)}
            disabled={preference.isSaving}
            className={`digest-frequency-option ${
              preference.frequency === option.value ? 'active' : ''
            } ${preference.isSaving ? 'saving' : ''}`}
            title={option.description}
          >
            {preference.isSaving && preference.frequency === option.value ? (
              <span className="digest-saving-indicator">
                <svg
                  className="animate-spin w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            ) : (
              option.label
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Global default frequency selector
 */
function GlobalFrequencySelector({
  value,
  onChange,
  isSaving,
}: {
  value: DigestFrequency;
  onChange: (frequency: DigestFrequency) => void;
  isSaving: boolean;
}) {
  return (
    <div className="digest-global-selector">
      <div className="digest-global-header">
        <h4 className="digest-global-title">Default Preference</h4>
        <p className="digest-global-description">
          Apply this setting to all groups at once
        </p>
      </div>
      <div className="digest-frequency-selector global">
        {frequencyOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            disabled={isSaving}
            className={`digest-frequency-option ${
              value === option.value ? 'active' : ''
            } ${isSaving ? 'saving' : ''}`}
            title={option.description}
          >
            {option.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(value)}
          disabled={isSaving}
          className={`digest-apply-all-btn ${isSaving ? 'saving' : ''}`}
        >
          {isSaving ? (
            <svg
              className="animate-spin w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : null}
          Apply to All
        </button>
      </div>
    </div>
  );
}

/**
 * DigestSettings Component
 *
 * Main component for managing email digest subscription preferences.
 * Fetches user's group memberships and allows configuring digest
 * frequency for each group.
 *
 * @returns The digest settings panel
 */
export function DigestSettings({ className }: DigestSettingsProps) {
  const [preferences, setPreferences] = useState<GroupDigestPreference[]>([]);
  const [globalFrequency, setGlobalFrequency] = useState<DigestFrequency>('weekly');
  const [isLoading, setIsLoading] = useState(true);
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /**
   * Fetch folders and groups to build preferences list
   */
  const fetchPreferences = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const folders = await foldersApi.list();

      // Build preferences list from folders and groups
      const prefs: GroupDigestPreference[] = [];

      for (const folder of folders) {
        if (folder.groups) {
          for (const group of folder.groups) {
            prefs.push({
              groupId: group.id,
              groupName: group.name,
              folderId: folder.id,
              folderName: folder.name,
              // Default to weekly, can be fetched from group membership later
              frequency: (group as Group & { digestFrequency?: DigestFrequency }).digestFrequency || 'weekly',
              isSaving: false,
            });
          }
        }
      }

      setPreferences(prefs);
    } catch (err) {
      setError('Failed to load groups. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  /**
   * Handle frequency change for a single group
   */
  const handleFrequencyChange = async (
    groupId: string,
    frequency: DigestFrequency
  ) => {
    // Find and update the preference
    setPreferences((prev) =>
      prev.map((p) =>
        p.groupId === groupId ? { ...p, isSaving: true } : p
      )
    );

    try {
      await groupsApi.updateDigestPreference(groupId, frequency);

      setPreferences((prev) =>
        prev.map((p) =>
          p.groupId === groupId
            ? { ...p, frequency, isSaving: false }
            : p
        )
      );

      setSuccessMessage('Preference saved');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      // Revert on error
      setPreferences((prev) =>
        prev.map((p) =>
          p.groupId === groupId ? { ...p, isSaving: false } : p
        )
      );
      setError('Failed to save preference. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  /**
   * Apply global frequency to all groups
   */
  const handleApplyGlobalFrequency = async (frequency: DigestFrequency) => {
    setGlobalFrequency(frequency);
    setIsApplyingAll(true);
    setError(null);

    try {
      // Update all groups in parallel
      await Promise.all(
        preferences.map((pref) =>
          groupsApi.updateDigestPreference(pref.groupId, frequency)
        )
      );

      // Update all preferences in state
      setPreferences((prev) =>
        prev.map((p) => ({ ...p, frequency }))
      );

      setSuccessMessage('All preferences updated');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      setError('Failed to update some preferences. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsApplyingAll(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`digest-settings ${className || ''}`}>
        <DigestSettingsSkeleton />
      </div>
    );
  }

  // Error state
  if (error && preferences.length === 0) {
    return (
      <div className={`digest-settings ${className || ''}`}>
        <DigestSettingsError message={error} onRetry={fetchPreferences} />
      </div>
    );
  }

  // Empty state
  if (preferences.length === 0) {
    return (
      <div className={`digest-settings ${className || ''}`}>
        <DigestSettingsEmptyState />
      </div>
    );
  }

  return (
    <div className={`digest-settings ${className || ''}`}>
      {/* Header */}
      <div className="digest-settings-header">
        <div className="digest-settings-icon">
          <svg
            className="w-6 h-6 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <div>
          <h3 className="digest-settings-title">Email Digest Settings</h3>
          <p className="digest-settings-description">
            Choose how often you want to receive email summaries for each message board group.
          </p>
        </div>
      </div>

      {/* Success/Error messages */}
      {successMessage && (
        <div className="digest-message success">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          {successMessage}
        </div>
      )}

      {error && preferences.length > 0 && (
        <div className="digest-message error">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Global default selector */}
      <GlobalFrequencySelector
        value={globalFrequency}
        onChange={handleApplyGlobalFrequency}
        isSaving={isApplyingAll}
      />

      {/* Per-group settings */}
      <div className="digest-groups-section">
        <h4 className="digest-groups-title">Per-Group Settings</h4>
        <div className="digest-groups-list">
          {preferences.map((pref) => (
            <GroupDigestRow
              key={pref.groupId}
              preference={pref}
              onFrequencyChange={handleFrequencyChange}
            />
          ))}
        </div>
      </div>

      {/* Info footer */}
      <div className="digest-settings-footer">
        <svg
          className="w-4 h-4 text-white/40"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>
          Digest emails include AI-generated summaries of recent discussions in your subscribed groups.
        </span>
      </div>
    </div>
  );
}

export default DigestSettings;
